import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Bell, TrendingUp, TrendingDown, Shield, Gift, Users, Zap, X, Settings } from 'lucide-react';

interface Notification {
  id: string;
  type: 'price-alert' | 'prediction' | 'social' | 'achievement' | 'security' | 'reward';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  actionUrl?: string;
  data?: any;
}

interface SmartNotificationsProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SmartNotifications({ isOpen, onClose }: SmartNotificationsProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filter, setFilter] = useState<'all' | 'unread' | 'high'>('all');

  useEffect(() => {
    // Simulate real-time notifications
    const mockNotifications: Notification[] = [
      {
        id: '1',
        type: 'price-alert',
        title: 'BTC Price Alert',
        message: 'Bitcoin reached your target price of $45,000! Time to consider your strategy.',
        timestamp: new Date(Date.now() - 5 * 60 * 1000),
        read: false,
        priority: 'high',
        data: { symbol: 'BTC', price: 45000, change: 3.2 }
      },
      {
        id: '2',
        type: 'prediction',
        title: 'Prediction Result',
        message: 'Your ETH prediction was correct! You earned 150 NDH. Accuracy: 89%',
        timestamp: new Date(Date.now() - 15 * 60 * 1000),
        read: false,
        priority: 'medium',
        data: { reward: 150, accuracy: 89 }
      },
      {
        id: '3',
        type: 'social',
        title: 'New Copy Trader',
        message: 'CryptoWhale_2024 started copying your trades. +5 reputation points!',
        timestamp: new Date(Date.now() - 30 * 60 * 1000),
        read: false,
        priority: 'medium',
        data: { username: 'CryptoWhale_2024', reputation: 5 }
      },
      {
        id: '4',
        type: 'achievement',
        title: 'Achievement Unlocked!',
        message: 'Diamond Hands: You\'ve held NDH for 100 days! Claim your exclusive NFT.',
        timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000),
        read: false,
        priority: 'high',
        data: { achievement: 'Diamond Hands', nft: true }
      },
      {
        id: '5',
        type: 'security',
        title: 'Security Alert',
        message: 'New device login detected from New York. If this wasn\'t you, secure your account.',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
        read: true,
        priority: 'urgent',
        data: { location: 'New York', device: 'Chrome on Windows' }
      },
      {
        id: '6',
        type: 'reward',
        title: 'Staking Rewards',
        message: 'You received 25.7 NDH from staking rewards. Total this month: 387.5 NDH',
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
        read: true,
        priority: 'low',
        data: { amount: 25.7, monthly: 387.5 }
      }
    ];

    setNotifications(mockNotifications);
  }, []);

  const getTypeIcon = (type: Notification['type']) => {
    switch (type) {
      case 'price-alert': return <TrendingUp className="h-4 w-4" />;
      case 'prediction': return <Zap className="h-4 w-4" />;
      case 'social': return <Users className="h-4 w-4" />;
      case 'achievement': return <Gift className="h-4 w-4" />;
      case 'security': return <Shield className="h-4 w-4" />;
      case 'reward': return <Gift className="h-4 w-4" />;
      default: return <Bell className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type: Notification['type']) => {
    switch (type) {
      case 'price-alert': return 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300';
      case 'prediction': return 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300';
      case 'social': return 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-300';
      case 'achievement': return 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-300';
      case 'security': return 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-300';
      case 'reward': return 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900 dark:text-emerald-300';
      default: return 'bg-gray-100 text-gray-600 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  const getPriorityColor = (priority: Notification['priority']) => {
    switch (priority) {
      case 'urgent': return 'border-l-4 border-red-500';
      case 'high': return 'border-l-4 border-orange-500';
      case 'medium': return 'border-l-4 border-blue-500';
      case 'low': return 'border-l-4 border-gray-300';
      default: return '';
    }
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  const markAsRead = (id: string) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === id ? { ...notif, read: true } : notif
      )
    );
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  const filteredNotifications = notifications.filter(notif => {
    switch (filter) {
      case 'unread': return !notif.read;
      case 'high': return notif.priority === 'high' || notif.priority === 'urgent';
      default: return true;
    }
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-background border-l shadow-lg z-50 overflow-hidden">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              <h3 className="font-semibold">Notifications</h3>
              {unreadCount > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {unreadCount}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm">
                <Settings className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-2 mt-3">
            {['all', 'unread', 'high'].map((filterType) => (
              <Button
                key={filterType}
                variant={filter === filterType ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(filterType as typeof filter)}
                className="text-xs"
              >
                {filterType === 'all' ? 'All' : 
                 filterType === 'unread' ? 'Unread' : 'Priority'}
              </Button>
            ))}
          </div>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
              <Bell className="h-8 w-8 mb-2" />
              <p>No notifications</p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 border-b hover:bg-accent/50 transition-colors cursor-pointer ${
                    !notification.read ? 'bg-blue-50/50 dark:bg-blue-950/50' : ''
                  } ${getPriorityColor(notification.priority)}`}
                  onClick={() => markAsRead(notification.id)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${getTypeColor(notification.type)}`}>
                      {getTypeIcon(notification.type)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm truncate">
                          {notification.title}
                        </h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeNotification(notification.id);
                          }}
                          className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                      
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {notification.message}
                      </p>
                      
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-muted-foreground">
                          {formatTime(notification.timestamp)}
                        </span>
                        {!notification.read && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full" />
                        )}
                      </div>

                      {/* Type-specific data rendering */}
                      {notification.type === 'price-alert' && notification.data && (
                        <div className="mt-2 p-2 bg-muted rounded text-xs">
                          <span className="font-medium">{notification.data.symbol}</span>
                          <span className={`ml-2 ${notification.data.change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {notification.data.change > 0 ? '+' : ''}{notification.data.change}%
                          </span>
                        </div>
                      )}

                      {notification.type === 'achievement' && notification.data?.nft && (
                        <Button size="sm" className="w-full mt-2 text-xs">
                          Claim NFT
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t">
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full"
            onClick={() => {
              setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            }}
          >
            Mark All as Read
          </Button>
        </div>
      </div>
    </div>
  );
}